/**
 * @author Ashish Dwivedi <ashish.dwivedi@314ecorp.com>
 * @description NotificationBar
 */
import _ from 'lodash';
import { Alert, Space, Tooltip, Typography } from 'antd';

import Button from 'components/widgets/button';
import { defaultPagination } from './InvoiceTable';
import useInvoiceHistory from 'hooks/admin/useInvoiceHistory';
import { shouldRetryPayment } from 'utils/admin/subscriptions';
import { useAppStoreValues } from 'store/app';
import { usePersistenceStoreActions, usePersistenceStoreValues } from 'store/persistence';

const MESSAGE = 'Your last payment was unsuccessful. Please update your payment method as soon as possible.';
const TOOLTIP_MESSAGE = 'Please contact your Super Admin';

/**
 * NotificationBar Component
 */
const NotificationBar: React.FC = () => {
	const { data, isLoading } = useInvoiceHistory(defaultPagination.pageNumber, defaultPagination.pageSize);
	const { userAccess } = useAppStoreValues();
	const { failedInvoiceId } = usePersistenceStoreValues();
	const { setFailedInvoiceId } = usePersistenceStoreActions();
	const latestPayment = _.first(data?.invoices);
	const currentInvoiceId = latestPayment?.external_id;
	const isDismissed = !!currentInvoiceId && currentInvoiceId === failedInvoiceId;
	const isBillingUser = userAccess.isBillingUser;

	if (!isLoading && !isDismissed && shouldRetryPayment(!!latestPayment, latestPayment)) {
		return (
			<Alert
				type='error'
				closable={userAccess.isInternalAdmin}
				onClose={() => setFailedInvoiceId(currentInvoiceId || '')}
				description={<Typography.Text type='danger'>{MESSAGE}</Typography.Text>}
				action={
					<Space>
						<Tooltip title={isBillingUser ? TOOLTIP_MESSAGE : undefined}>
							<Button.RetryPayment externalId={latestPayment?.external_id} disabled={isBillingUser} />
						</Tooltip>
						<Tooltip title={isBillingUser ? TOOLTIP_MESSAGE : undefined}>
							<Button.OpenStripePortal buttonType='failure' disabled={isBillingUser} />
						</Tooltip>
					</Space>
				}
				className='mb-5 items-center px-5 py-2.5'
			/>
		);
	}

	return null;
};

export default NotificationBar;
